import { useEffect, useState } from "react";
import { Menu, X, Phone, Mail } from "lucide-react";
import { Link, NavLink } from "react-router-dom";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/partners", label: "Partners" },
  { to: "/resources", label: "Resources" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact Us" },
];

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Livery stripe */}
      <div className="livery-stripe" />

      {/* Utility bar */}
      <div className="hidden lg:block bg-black text-white/80 border-b border-white/10">
        <div className="container mx-auto px-4 flex items-center justify-between text-xs tracking-wide py-1.5">
          <div className="flex items-center gap-6">
            <a href="tel:+254715055105" className="flex items-center gap-2 hover:text-[#73BDF2] transition-colors duration-300">
              <Phone size={12} className="text-[#73BDF2]" />
              +254 715 055 105
            </a>
            <a href="mailto:info@moreflexaviation.com" className="flex items-center gap-2 hover:text-[#73BDF2] transition-colors duration-300">
              <Mail size={12} className="text-[#73BDF2]" />
              info@moreflexaviation.com
            </a>
          </div>
          <p className="uppercase tracking-[0.25em] text-[10px] text-white/50">
            24/7 VIP Airport Support
          </p>
        </div>
      </div>

      {/* Main nav */}
      <div
        className={`bg-black/95 backdrop-blur-md text-white transition-shadow duration-300 ${
          isScrolled ? "shadow-elevated" : ""
        }`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center py-3">
          <Link to="/" className="flex items-center" onClick={closeMenu}>
            <img
              src="/Final_logo_6.png"
              alt="Moreflex Aviation"
              className="h-11 md:h-12"
              loading="lazy"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-[#73BDF2]" : "text-white/85 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {item.label}
                    <span
                      className={`absolute -bottom-1 left-0 h-[2px] bg-[#73BDF2] transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Desktop CTA */}
          <Link to="/contact" className="hidden lg:inline-flex btn-primary">
            Let's Talk
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2 -mr-2"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={closeMenu}
        />
        <div
          className={`absolute top-0 right-0 h-full w-[82%] max-w-sm bg-black text-white shadow-2xl transition-transform duration-300 flex flex-col ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="livery-stripe" />
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
            <img src="/Final_logo_6.png" alt="Moreflex Aviation" className="h-10" loading="lazy" />
            <button onClick={closeMenu} aria-label="Close menu" className="p-1">
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col px-6 py-6 gap-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-white/5 transition-colors duration-300 ${
                    isActive ? "text-[#73BDF2]" : "text-white/85 hover:text-[#73BDF2]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto px-6 py-6 border-t border-white/10 space-y-4">
            <a href="tel:+254715055105" className="flex items-center gap-3 text-sm text-white/80">
              <Phone size={16} className="text-[#73BDF2]" />
              +254 715 055 105
            </a>
            <a href="mailto:info@moreflexaviation.com" className="flex items-center gap-3 text-sm text-white/80">
              <Mail size={16} className="text-[#73BDF2]" />
              info@moreflexaviation.com
            </a>
            <Link to="/contact" className="btn-primary w-full" onClick={closeMenu}>
              Let's Talk
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
